import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { User, Shield, Truck, Briefcase } from 'lucide-react';
import axios from 'axios';

const API_URL = '/api';

const roleIcon = (role) => {
  if (role === 'admin') return <Shield size={16} className="text-purple-600" />;
  if (role === 'office_staff') return <Briefcase size={16} className="text-blue-600" />;
  return <Truck size={16} className="text-amber-600" />;
};

const roleLabel = (role) => {
  if (role === 'admin') return 'Admin';
  if (role === 'office_staff') return 'Office Staff';
  return 'Driver';
};

const roleBadgeCls = (role) => {
  if (role === 'admin') return 'bg-purple-100 text-purple-700';
  if (role === 'office_staff') return 'bg-blue-100 text-blue-700';
  return 'bg-amber-100 text-amber-700';
};

export default function Login() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loggingIn, setLoggingIn] = useState(false);
  const [error, setError] = useState('');
  const { setUserDirect } = useAuth();
  const navigate = useNavigate();

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/auth/users-list`);
      setUsers(data);
    } catch (e) {
      console.error('Failed to load users:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleLogin = async (user) => {
    setError('');
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API_URL}/auth/quick-login`, { user_id: user.id }, { withCredentials: true });
      // Store tokens in localStorage
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
      }
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      // Set user directly from response — no need for a round-trip /auth/me call
      setUserDirect({
        id: data.id,
        name: data.name,
        role: data.role,
        email: data.email,
        created_at: data.created_at,
        is_active: data.is_active
      });
      if (user.role === 'driver') {
        navigate('/driver/jobs');
      } else {
        navigate('/dashboard');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Login failed');
    } finally {
      setLoggingIn(false);
    }
  };

  const officeUsers = users.filter(u => u.role === 'admin' || u.role === 'office_staff');
  const driverUsers = users.filter(u => u.role === 'driver');

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center p-6" data-testid="login-page">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/cargodash-logo.svg" alt="CargoDash" className="h-14 mx-auto mb-4" />
          <p className="text-sm text-[#6B7280]">Digital Trip Sheets & Waybills</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4" data-testid="login-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0D9488] border-t-transparent"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Office / Admin Users */}
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h2 className="text-sm font-semibold text-[#1A1A2E] mb-3 flex items-center gap-2">
                <Briefcase size={16} className="text-[#6B7280]" /> Office & Admin
              </h2>
              <div className="space-y-1.5">
                {officeUsers.length === 0 ? (
                  <p className="text-sm text-[#9CA3AF] py-2">No office users created yet</p>
                ) : officeUsers.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => handleLogin(user)}
                    disabled={loggingIn}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left hover:bg-teal-50 transition-colors group border border-transparent hover:border-teal-200 disabled:opacity-50"
                    data-testid={`login-user-${user.id}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-gray-100 group-hover:bg-teal-100 flex items-center justify-center transition-colors">
                      {roleIcon(user.role)}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#1A1A2E] group-hover:text-[#0D9488]">{user.name}</p>
                    </div>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${roleBadgeCls(user.role)}`}>
                      {roleLabel(user.role)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Driver Users */}
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h2 className="text-sm font-semibold text-[#1A1A2E] mb-3 flex items-center gap-2">
                <Truck size={16} className="text-[#6B7280]" /> Drivers
              </h2>
              <div className="space-y-1.5">
                {driverUsers.length === 0 ? (
                  <p className="text-sm text-[#9CA3AF] py-2">No drivers created yet</p>
                ) : driverUsers.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => handleLogin(user)}
                    disabled={loggingIn}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left hover:bg-amber-50 transition-colors group border border-transparent hover:border-amber-200 disabled:opacity-50"
                    data-testid={`login-user-${user.id}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-gray-100 group-hover:bg-amber-100 flex items-center justify-center transition-colors">
                      <Truck size={16} className="text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#1A1A2E] group-hover:text-amber-700">{user.name}</p>
                    </div>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                      Driver
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
